import type { ReactNode } from 'react'
import type { LairThreatDto } from '@/api/hooks'
import { HintBubble, ResourceTally } from '@/components/game/ui'
import {
  ScopeIcon, StatIcon, PennantIcon, ShipIcon, GoldIcon,
} from '@/components/game/GameIcons'

/**
 * "Rations équipage" label with a hover tooltip explaining rhum rationing — same
 * spirit as the CombatPrimer, inline. Used wherever a mission's rhum cost shows.
 */
export function RationsLabel() {
  return (
    <span className="group/tip relative cursor-help underline decoration-dotted decoration-ocean-500/50 underline-offset-2">
      Rations équipage
      <HintBubble
        className="w-60"
        label={
          <>
            <span className="font-semibold text-sand-200">Rations équipage</span> : le rhum bu pendant la
            traversée, soit l'<span className="font-semibold text-sand-200">équipage</span> du navire multiplié
            par les heures de mer. C'est le nombre de marins qui compte, pas la taille de la coque : un galion
            embarque 40 hommes, un Navire Légendaire seulement 30, et coûte donc moins cher à nourrir. Chaque
            navire est plafonné à sa propre cale (on n'emporte pas plus de grog qu'on ne peut en ranger). Sans
            assez de rhum en banque, impossible d'appareiller.
          </>
        }
      />
    </span>
  )
}

/**
 * Crossing-event risk, surfaced where the captain commits to a real voyage (any
 * away target) rather than in the first-patrol onboarding card — the patrol is a
 * deliberately calm run, and warning a brand-new player before their first
 * pleasure backfires. Voyage events fire for every mission type except Patrol and
 * Scouting, scaling with trip length (see MissionCompletionService::applyVoyageEvent).
 */
export function VoyageRiskNote() {
  return (
    <p className="text-xs text-ocean-300 leading-relaxed border-l-2 border-rum-500/40 pl-3">
      ⚠️ Sur les longues traversées, des événements peuvent survenir : kraken, tempêtes, scorbut…
      Plus le trajet est long, plus le risque grandit.
    </p>
  )
}

/**
 * A labeled wrapper around an action panel. Where several options coexist on the
 * same island (commerce / contrebande / raid), the heading tells them apart at a
 * glance — they are genuinely different gameplay choices, not duplicates.
 */
export function PanelSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ocean-300">{label}</p>
      {children}
    </div>
  )
}

export function CargoInput({
  label,
  Icon,
  value,
  max,
  error,
  onChange,
}: {
  label: string
  Icon: (p: { className?: string }) => ReactNode
  value: number
  max: number
  error: boolean
  onChange: (v: number) => void
}) {
  return (
    <label className="block text-xs text-ocean-200">
      <span className="inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5 text-sand-300" /> {label}</span> <span className="text-ocean-400">(max {max})</span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
        className={`mt-1 w-full px-2 py-1 rounded bg-ocean-900 border focus:outline-none text-sm ${
          error ? 'border-rum-500' : 'border-ocean-500/40 focus:border-sand-300'
        }`}
      />
    </label>
  )
}

const SPECIALITY_LABEL: Record<string, string> = {
  bordee: 'Artillerie (bordée)',
  abordage: 'Abordage',
  balanced: 'Polyvalent',
}

/**
 * Defence intel readout for a raidable target — dice per phase, pierce reach, the
 * toughest hull's armure and the defending fleet's combat style. Shared by the
 * NPC lair raid and the (scouted) PvP raid: the same `LairThreatDto` powers both.
 */
export function ThreatReadout({ threat }: { threat: LairThreatDto }) {
  return (
    <div className="text-xs bg-rum-900/30 rounded p-2 border border-rum-500/20 space-y-1.5">
      <p className="text-rum-300 uppercase tracking-wider text-[10px] flex items-center gap-1.5"><ScopeIcon className="w-3.5 h-3.5" /> Renseignements</p>

      <div className="flex justify-between text-ocean-200 tabular-nums">
        <span>Leur frappe</span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex items-center gap-1" title="Bordée (artillerie)"><StatIcon name="bordee" className="w-3.5 h-3.5" />{threat.bordee}</span>
          <span className="inline-flex items-center gap-1" title="Abordage"><StatIcon name="abordage" className="w-3.5 h-3.5" />{threat.abordage}</span>
        </span>
      </div>

      <div className="flex justify-between text-ocean-200">
        <span>Pénétration</span>
        <span className="tabular-nums text-sand-300">jusqu'à l'armure {threat.offensivePierce}</span>
      </div>
      <p className="text-ocean-400 text-[11px] leading-snug">
        Tes coques d'armure &gt; {threat.offensivePierce} encaissent sans broncher.
      </p>

      <div className="flex justify-between text-ocean-200">
        <span>Leur coque la plus dure</span>
        <span className="tabular-nums inline-flex items-center gap-1"><StatIcon name="armure" className="w-3.5 h-3.5" /> armure {threat.maxArmure}</span>
      </div>
      <div className="flex justify-between text-ocean-200">
        <span>Style</span>
        <span className="text-sand-300">{SPECIALITY_LABEL[threat.speciality]}</span>
      </div>

      <p className="text-ocean-400 text-[11px] leading-snug pt-1">
        {threat.shipCount} navire{threat.shipCount > 1 ? 's' : ''}
        {threat.hasFort ? ' + un fort côtier' : ''}. Il te faut bordée ou abordage ≥ {threat.maxArmure} pour percer leur meilleure coque.
        {' '}
        <span className="group/tip relative cursor-help underline decoration-dotted decoration-ocean-500/50 underline-offset-2 text-ocean-300">
          détail des dés
          <HintBubble
            label={
              <>
                Dés lancés par phase : <span className="text-sand-200">Manœuvre {threat.manoeuvre}</span> (initiative : décide qui tire en premier, n'inflige aucun dégât) · Bordée {threat.bordee} · Abordage {threat.abordage}.
              </>
            }
          />
        </span>
      </p>
    </div>
  )
}

/**
 * "Intel du JJ/MM" — a small staleness marker shown beside a scouted PvP target's
 * threat readout. The éclaireur's snapshot is FROZEN at scout-time (durable
 * opacity: the defender can rebuild without the readout updating), so we surface
 * WHEN the intel was taken — the attacker knows it may no longer be accurate.
 */
export function IntelStalenessMarker({ scoutedAt }: { scoutedAt: string }) {
  const date = new Date(scoutedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
  return (
    <p className="text-[11px] text-ocean-400 italic flex items-center gap-1">
      <ScopeIcon className="w-3 h-3 text-ocean-400/80" />
      <span>Intel du {date}, peut être périmé.</span>
    </p>
  )
}

/**
 * Ré-export de compatibilité : la liste vit désormais dans `shipPicker/`, avec
 * son filtre par coque et par module, son tri et sa sélection en masse. Les six
 * lanceurs qui l'importaient d'ici, et le ré-export de `pages/Map.tsx`,
 * continuent de marcher sans changement.
 *
 * La colonne de droite (les dés de combat et l'armure) qui était en dur ici est
 * devenue une prop de rendu `meta`, ce qui permet à la même brique de servir
 * aussi les écrans de cale et le convoi de carrière.
 */
export { ShipPickerList } from '@/components/game/map/shipPicker/ShipPickerList'

/**
 * Ambush-risk tiering for the commerce launcher's pre-departure chip (spec
 * §4.3: announce the risk BEFORE departure so the escort-or-risk-it decision
 * happens at launch time, not after). Commerce ambush is now rare & greedy —
 * driven by distance × cargo fill, capped at COMMERCE_RISK_CAP (0.18), not by
 * destination danger (see SeaAmbush::commerceProbability). With that lower
 * cap, "élevé" only ever covers the 0.16-0.18 band — the longest, fullest
 * hauls — while a short/light run stays "faible" (a few percent). Standard
 * Tailwind semantic colours (green/amber/red) — NOT the partial custom
 * ocean/sand/rum shades, which silently no-op outside their defined steps.
 */
function ambushRiskTier(risk: number): { label: string; badge: string } {
  if (risk >= 0.16) {
    return { label: 'élevé', badge: 'bg-ocean-900 text-red-400 border-red-500' }
  }
  if (risk >= 0.08) {
    return { label: 'modéré', badge: 'bg-ocean-900 text-amber-300 border-amber-400' }
  }
  return { label: 'faible', badge: 'bg-ocean-900 text-green-300 border-green-400' }
}

/** French label for a backend ship-type enum string, for the ambush party
 * composition chips (no per-ship DTO here — just `{shipType, count}` — so we
 * can't reuse `shipTypeLabel`, which the server only attaches to owned-ship
 * DTOs). Mirrors the labels used across Fleet/Codex. */
const SHIP_TYPE_LABEL: Record<string, string> = {
  brick: 'Brick',
  fregate: 'Frégate',
  galion: 'Galion',
  legendary: 'Navire Légendaire',
}

/** Threat-tier styling for `ambushThreat` (win-odds proxy, faible/modérée/forte)
 * — distinct scale from `ambushRiskTier` (a probability), but same semantic
 * colours (green=safe, amber=caution, red=danger) + dot-strength affordance
 * from the mockup's Proposition C. */
function ambushThreatTier(threat: 'faible' | 'modérée' | 'forte'): { badge: string; dots: number } {
  if (threat === 'forte') return { badge: 'text-red-400', dots: 3 }
  if (threat === 'modérée') return { badge: 'text-amber-300', dots: 2 }
  return { badge: 'text-green-300', dots: 1 }
}

/** Strength dots (Proposition C mockup's `.dots` affordance) — filled amber
 * dots up to the threat tier, empty beyond. */
function ThreatDots({ filled }: { filled: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className={`w-1.5 h-1.5 rounded-full ${i <= filled ? 'bg-amber-300' : 'bg-white/15'}`}
        />
      ))}
    </span>
  )
}

/**
 * Pre-departure "carte de menace" for the commerce launcher (user-validated
 * Proposition C, spec §4.3 follow-up) — a dedicated card pairing the two
 * INDEPENDENT ambush signals: `ambushRisk` (probability, driven by distance ×
 * cargo fill) and `ambushThreat` (win-odds tier, the selected fleet vs the
 * pirate party the haul's gold value would attract), plus the party's real
 * composition below as ship chips. Hidden by the caller when risk is 0
 * (empty cargo, or under the new-comer shield — no ambush possible either
 * way): a long/full haul can face a WEAK party (small comptoir) while a
 * short/light one faces a STRONG one (rich haul) — a single gauge would mask
 * that trade-off. */
export function AmbushThreatCard({
  risk,
  threat,
  fleet,
  title = "Menace d'embuscade",
  tooltipLabel = 'Comment fonctionne une embuscade ?',
  tooltip,
}: {
  risk: number
  threat: 'faible' | 'modérée' | 'forte' | undefined
  fleet: { shipType: string; count: number }[] | undefined
  /** Card heading — defaults to the commerce ambush; the contraband launcher passes "Menace d'interception". */
  title?: string
  /** Accessible label for the "?" affordance. */
  tooltipLabel?: string
  /** Body of the "?" hint bubble — defaults to the sea-ambush explanation. */
  tooltip?: ReactNode
}) {
  const riskTier = ambushRiskTier(risk)
  const resolvedThreat = threat ?? 'forte'
  const threatTier = ambushThreatTier(resolvedThreat)
  const tooltipBody = tooltip ?? (
    <>
      <b className="text-sand-100">Embuscade en mer.</b> Le risque monte avec la distance et le
      remplissage de ta cale ; la force de la meute avec la valeur du butin. Repoussée, tu gardes
      ta cargaison (+ une prime d'épaves) ; vaincue, tu perds la cargaison et un navire peut couler.
      Une escorte de combat améliore tes chances.
    </>
  )

  return (
    <div className="bg-red-500/[0.06] border border-red-500/25 rounded-lg p-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sand-100 text-xs inline-flex items-center gap-1.5">
          <PennantIcon className="w-3.5 h-3.5" /> {title}
          <span className="group/tip relative inline-flex">
            <button
              type="button"
              aria-label={tooltipLabel}
              className="grid place-items-center w-4 h-4 rounded-full border border-ocean-300 text-ocean-300 text-[11px] leading-none cursor-help focus:outline-none focus-visible:ring-1 focus-visible:ring-ocean-300"
            >
              ?
            </button>
            <HintBubble
              placement="bottom"
              align="start"
              label={tooltipBody}
            />
          </span>
        </span>
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-semibold uppercase tracking-wider ${riskTier.badge}`}>
          {riskTier.label}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-ocean-900/50 border border-sand-300/15 rounded-md p-2">
          <p className="text-[10px] uppercase tracking-wider text-ocean-300">Probabilité</p>
          <p className="font-display text-sm text-sand-100 mt-0.5 inline-flex items-center gap-1.5 tabular-nums">
            <ScopeIcon className="w-3.5 h-3.5 text-ocean-300" /> {Math.round(risk * 100)}%
          </p>
        </div>
        <div className="bg-ocean-900/50 border border-sand-300/15 rounded-md p-2">
          <p className="text-[10px] uppercase tracking-wider text-ocean-300">Force adverse</p>
          <p className={`font-display text-sm mt-0.5 inline-flex items-center gap-1.5 ${threatTier.badge}`}>
            <ThreatDots filled={threatTier.dots} /> {resolvedThreat}
          </p>
        </div>
      </div>
      {fleet !== undefined && fleet.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {fleet.map((f) => (
            <span
              key={f.shipType}
              className="inline-flex items-center gap-1 bg-ocean-900/60 border border-sand-300/15 rounded px-1.5 py-0.5 text-[11px] text-sand-100"
            >
              <ShipIcon type={f.shipType} className="w-3.5 h-3.5 text-sand-300" />
              {SHIP_TYPE_LABEL[f.shipType] ?? f.shipType} <span className="text-ocean-200 tabular-nums">×{f.count}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Loot / sale breakdown shown under every preview. With a full breakdown it reads:
 *   base X · réput +Y · flotte +Z · faux-fond +M · total W
 * `réput` is the reputation contribution (allegiance bonus); `flotte` the
 * hull-power contribution (raid only — heavier hulls plunder more, Lever D);
 * `faux-fond` the convoy-best Faux-fond contribution (smuggle only).
 * Each segment renders only when present, so commerce shows `base · réput · total`
 * and a raid adds the `flotte` segment when the fleet earns one. Bonuses read
 * bold + sand with a gold coin so the levers are legible; a 0 réput still shows
 * (the lever exists), while `flotte` and `faux-fond` are hidden at 0 (no such
 * lever armed). Numbers are server-computed; the front only renders.
 */
export function ReputBreakdown({
  base,
  reputationBonus,
  hullBonus,
  moduleBonus,
  total,
}: {
  base?: number
  reputationBonus: number
  hullBonus?: number
  moduleBonus?: number
  total?: number
}) {
  const Bonus = (
    <span
      className="inline-flex items-center gap-1 font-semibold text-sand-300"
      title="Prime de réputation : ton allégeance (Doctrine + notoriété hors-la-loi) gonfle le gain."
    >
      réput {reputationBonus >= 0 ? '+' : ''}{reputationBonus.toLocaleString('fr-FR')} <GoldIcon className="w-3 h-3" />
    </span>
  )
  return (
    <p className="text-[11px] text-ocean-300 flex items-center gap-1.5 flex-wrap tabular-nums">
      {base !== undefined && (
        <>
          <span className="inline-flex items-center gap-1">base <ResourceTally gold={base} className="text-ocean-200" /></span>
          <span aria-hidden="true" className="text-ocean-500">·</span>
        </>
      )}
      {Bonus}
      {(hullBonus ?? 0) > 0 && (
        <>
          <span aria-hidden="true" className="text-ocean-500">·</span>
          <span
            className="inline-flex items-center gap-1 font-semibold text-sand-300"
            title="Bonus de flotte : calculé sur la puissance MOYENNE de tes coques, pas sur leur nombre. Une flotte de frégates, galions ou légendaires pille davantage qu'une nuée de bricks ; ajouter un brick à une flotte lourde fait baisser la moyenne, donc le bonus."
          >
            flotte +{(hullBonus ?? 0).toLocaleString('fr-FR')} <GoldIcon className="w-3 h-3" />
          </span>
        </>
      )}
      {(moduleBonus ?? 0) > 0 && (
        <>
          <span aria-hidden="true" className="text-ocean-500">·</span>
          <span
            className="inline-flex items-center gap-1 font-semibold text-sand-300"
            title="Bonus de Faux-fond : le meilleur navire du convoi fence toute la cargaison à son taux."
          >
            faux-fond +{(moduleBonus ?? 0).toLocaleString('fr-FR')} <GoldIcon className="w-3 h-3" />
          </span>
        </>
      )}
      {total !== undefined && (
        <>
          <span aria-hidden="true" className="text-ocean-500">·</span>
          <span className="inline-flex items-center gap-1 text-sand-200">total <ResourceTally gold={total} className="font-semibold" /></span>
        </>
      )}
    </p>
  )
}
